<template>
    <div>
        <div class="container">
            <Breadcrumb
                :items="[
                    [$t('core.services'), 'services'],
                    [model.services.find((service) => service.slug === $route.params.service).name, 'service'],
                    [model.title, 'price'],
                ]"
                is-on-page-top
            />
            <div class="grid grid-cols-12 mt-5 lg:mt-8 md:mb-28">
                <div class="col-span-12 lg:col-span-8">
                    <div class="hero" :style="`background-image: url(${model.cover_image})`">
                        <h1 class="title font-HKGrotesk-Bold">{{ model.title }}</h1>
                        <h2 v-if="model.subtitle" class="font-HKGrotesk-Bold text-base mt-4 text-dark-200">
                            {{ model.subtitle }}
                        </h2>
                    </div>

                    <div class="tinymce-content" v-html="model.details"></div>
                    <div v-if="model.before_after.length" class="mt-15 mb-24">
                        <h3 class="title-2 mb-5"><T t="pricing.previous_works" /></h3>

                        <div class="grid grid-cols-12">
                            <div
                                v-for="comparisonImage in model.before_after"
                                :key="`comparison-image-${comparisonImage.id}`"
                                class="col-span-12 md:col-span-6"
                            >
                                <ComparisonCard
                                    :label="comparisonImage.title"
                                    :before="comparisonImage.image_before"
                                    :after="comparisonImage.image_after"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="tinymce-content" v-html="model.details2"></div>

                    <div class="mt-20">
                        <span class="subtitle mb-5"><T t="pricing.prices" /></span>
                        <div
                            class="
                                pt-5
                                w-[calc(100%+32px)]
                                -ml-4
                                md:w-full md:ml-0
                                px-4
                                md:pt-8 md:px-7
                                pb-10
                                bg-light-300
                                rounded-none
                                md:rounded-lg
                            "
                        >
                            <h3 class="subtitle">{{ model.title }}</h3>
                            <div class="flex flex-col gap-y-2 mt-6 mb-8">
                                <PriceCard
                                    v-for="price in model.related_prices"
                                    :key="price.id"
                                    :title="price.title"
                                    :is-price-from="price.is_price_from"
                                    borderless
                                    :price="price.price"
                                />
                            </div>
                            <span class="info-title"><T t="pricing.good_to_know" /></span>
                            <div class="md:pl-8">
                                <div class="tinymce-content" v-html="model.content"></div>
                            </div>
                            <ButtonLink
                                class="block mx-auto w-full md:w-max p-button-md/lg mt-10"
                                :label="$t('pricing.all_prices')"
                                :to="localePath('pricing')"
                            />
                        </div>
                    </div>
                </div>

                <BookAppointmentCard :url="model.medicall_link" />
            </div>
        </div>

        <CustomerReviewCarousel
            v-if="testimonials && testimonials.length"
            class="my-20 md:my-28"
            client
            :title="$t('dentist.customer_reviews')"
            :reviews="testimonials"
        />

        <div class="container">
            <ArticleCardGrid
                v-if="model.related_articles.length"
                :title="$t('generic.atricles_that_may_interest_you')"
                :articles="model.related_articles"
            />
        </div>
    </div>
</template>

<script>
import Breadcrumb from '@/components/Generic/Breadcrumb';
import ArticleCardGrid from '@/components/Sections/ArticleCardGrid/ArticleCardGrid';
import PriceCard from '@/components/Cards/PriceCard/PriceCard';
import ComparisonCard from '@/components/Cards/ComparisonCard/ComparisonCard';
import BookAppointmentCard from '@/components/Cards/BookAppointmentCard/BookAppointmentCard';
import CustomerReviewCarousel from '@/components/Carousels/CustomerReviewCarousel/CustomerReviewCarousel';

export default {
    name: 'Price',
    components: {
        Breadcrumb,
        ComparisonCard,
        PriceCard,
        CustomerReviewCarousel,
        BookAppointmentCard,
        ArticleCardGrid,
    },
    async asyncData({ $axios, params, i18n, error, redirect, localePath }) {
        if (params.price === '__redirect') {
            return await redirect(localePath('services'));
        }

        let model = null;
        let testimonials = null;

        try {
            await $axios
                .$get(`/service-price/by-slug/${params.price}`)
                .then((res) => {
                    if (res.success === 1) {
                        model = res.data;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });

            if (!model) {
                return error({ statusCode: 404, message: i18n.t('generic.page_not_found') });
            }

            await $axios
                .$get(`/testimonial?service_price_id=${model.id}`)
                .then((res) => {
                    if (res.success === 1) {
                        testimonials = res.data;
                    }
                })
                .catch((err) => {
                    console.log(err);
                });
        } catch (err) {
            console.log(err);
        }

        await i18n.generateLocalizedRouteParams(model, 'price');

        return { model, testimonials };
    },

    head() {
        return {
            title: this.model.title,
            meta: [
                {
                    hid: 'title',
                    name: 'title',
                    content: this.model.meta_title,
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: this.model.og_title,
                },
                {
                    hid: 'description',
                    name: 'description',
                    content: this.model.meta_description,
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: this.model.og_description,
                },
                {
                    property: 'article:published_time',
                    content: this.$dayjs(this.model.created_at * 1000).format('YYYY-MM-DD'),
                },
                {
                    name: 'publish-date',
                    content: this.$dayjs(this.model.created_at * 1000).format('YYYY-MM-DD'),
                },
            ],
        };
    },
    mounted() {
        this.articleCarousel = new this.$swiper('#article-carousel', {
            slidesPerView: 1.2,
            spaceBetween: 16,
            resistance: true,
            resistanceRatio: 0,
            allowTouchMove: this.$device.isMobileOrTablet || window.innerWidth < 768,
            loop: true,
        });
    },
    beforeDestroy() {
        if (this.articleCarousel) {
            this.articleCarousel.destroy();
        }
    },
};
</script>

<style scoped>
.hero {
    @apply px-4 pb-6 pt-24 md:pb-12 md:pl-16 md:pt-32 md:pr-48 z-10 mb-10 relative rounded-2xl overflow-hidden bg-center bg-cover;
    &::before {
        @apply block absolute h-full w-full left-0 top-0 -z-1;
        content: '';
        background: linear-gradient(
            239.41deg,
            rgba(255, 255, 255, 0.0001) 12.83%,
            rgba(255, 255, 255, 0.856561) 72.79%
        );
    }
}

.list li {
    &::before {
        content: '';
        @apply w-1 h-1 bg-dark-600 rounded-full block mr-2;
    }
}
</style>
